// src/inventory/components/Scanner.js
// Camera barcode/QR scanner for Inventory. Fully self-contained: renders a
// "Scan" button; tapping it opens a full-screen camera view, and a successful
// read calls onScan(text) and closes. Works on iPhone Safari and Android
// Chrome (HTTPS required, which the deployed app has). Uses html5-qrcode,
// which reads QR codes and standard barcodes (Code128, EAN, UPC, etc.).
import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

const REGION_ID = 'inventory-scanner-region';

function Scanner({ onScan }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const scannerRef = useRef(null);

  const stopScanner = async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (s) {
      try { await s.stop(); } catch (_) {}
      try { s.clear(); } catch (_) {}
    }
  };

  const closeOverlay = () => {
    stopScanner();
    setOpen(false);
    setError(null);
  };

  useEffect(() => {
    if (!open) return;
    const scanner = new Html5Qrcode(REGION_ID);
    scannerRef.current = scanner;
    scanner
      .start(
        { facingMode: 'environment' },           // back camera
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          if (navigator.vibrate) navigator.vibrate(80); // haptic tick (Android)
          onScan(decodedText);
          closeOverlay();
        },
        () => {} // per-frame misses are normal — ignore quietly
      )
      .catch((err) => {
        setError(
          String(err).includes('NotAllowedError')
            ? 'Camera permission denied. Allow camera access in your browser settings, or type the code manually in the search box.'
            : 'Could not start the camera. You can type the code manually in the search box.'
        );
      });
    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Scan a barcode or QR code"
        style={{
          padding: '10px 16px',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '1rem',
          whiteSpace: 'nowrap',
        }}
      >
        📷 Scan
      </button>

      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', padding: '16px',
          }}
        >
          <p style={{ color: '#ddd', marginBottom: '12px', fontWeight: 'bold' }}>
            Point the camera at a barcode or QR code
          </p>

          {/* html5-qrcode mounts the video feed into this div */}
          <div id={REGION_ID} style={{ width: '100%', maxWidth: '420px' }} />

          {error && (
            <p style={{ color: '#ff7b7b', marginTop: '14px', textAlign: 'center', maxWidth: '420px' }}>
              {error}
            </p>
          )}

          <button
            onClick={closeOverlay}
            style={{
              marginTop: '18px', padding: '12px 32px',
              backgroundColor: '#6c757d', color: 'white',
              border: 'none', borderRadius: '8px',
              cursor: 'pointer', fontSize: '1rem',
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </>
  );
}

export default Scanner;
